import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAPI } from '../contexts/APIContext';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import LoadingScreen from '../components/LoadingScreen';
import { showToast } from '../components/Toast';
import VideoPlayer from '../components/VideoPlayer';
import { getOriginalsOutro } from '../config/originalsOutros';
import { getOriginalsBranding } from '../config/originalsBranding';
import AuthWall from '../components/AuthWall';
import { useEntitlementGate } from '../contexts/EntitlementGateContext';
import { motion } from 'framer-motion';
import { PulseLoader } from 'react-spinners';
import logger from '../utils/logger';

// Helper function to get a meaningful episode title
const getEpisodeTitle = (episode, index, show) => {
  // If episode has a proper title that's not generic, use it
  if (episode.title && episode.title !== `${show?.name || 'Show'} Episode`) {
    return episode.title;
  }
  
  // If episode has a name field, use it
  if (episode.name && episode.name !== `${show?.name || 'Show'} Episode`) {
    return episode.name;
  }
  
  // Try to create a meaningful title from theme and description
  const theme = episode.theme;
  if (theme && theme.length > 0) {
    return `Episode ${index + 1}: ${theme}`;
  }
  
  // If there's a description, use first part as title
  if (episode.description && episode.description.trim().length > 0) {
    const firstSentence = episode.description.split('.')[0].trim();
    if (firstSentence.length > 0 && firstSentence.length <= 50) {
      return `Episode ${index + 1}: ${firstSentence}`;
    }
  }
  
  // Final fallback
  return `Episode ${index + 1}`;
};

// Helper function to render episode thumbnail based on status
const renderEpisodeThumbnail = (episode, index, isStoryverse = false) => {
  logger.log('🖼️ Rendering thumbnail for episode:', episode.id, 'Full episode object:', episode);
  logger.log('🔍 Episode status:', episode.status, 'Type:', typeof episode.status);
  logger.log('🔍 Episode file_url:', episode.file_url, 'Has file?', !!episode.file_url);
  logger.log('🔍 Status check - PROCESSING?', episode.status === 'PROCESSING', 'requested?', episode.status === 'requested');
  
  // Check if episode has a video file available
  const hasVideoFile = episode.file_url && episode.file_url.trim() !== '';

  // If episode has video file, show cover image as thumbnail (optimized)
  if (hasVideoFile) {
    logger.log('✅ Showing cover for episode:', episode.id, 'cover:', episode.cover_image_url);

    // Prefer an explicit cover/thumbnail image when present.
    const coverSrc = episode.cover_image_url || episode.thumbnail_url;
    if (coverSrc) {
      return (
        <img
          src={coverSrc}
          alt={`Episode ${index + 1}`}
          className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:brightness-110"
          loading="lazy"
        />
      );
    }

    // No cover image: show the video's first frame as the thumbnail
    // (preload="metadata" so a frame paints instead of a blank box).
    return (
      <video
        className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:brightness-110"
        muted
        loop
        playsInline
        preload="metadata"
      >
        <source src={`${episode.file_url}#t=0.1`} type="video/mp4" />
      </video>
    );
  }
  
  // If episode is still processing (no video file and processing status)
  const isProcessing = episode.status === 'PROCESSING' || 
                      episode.status === 'requested' || 
                      episode.status === 'pending' ||
                      episode.status === 'REQUESTED' ||
                      episode.status === 'PENDING';
  
  if (isProcessing) {
    logger.log('✅ Showing processing screen for episode:', episode.id);
    const percentage = episode.status_percentage || 0;
    return (
      <div className="w-full h-full bg-gradient-to-br from-purple-600 via-pink-500 to-orange-500 flex flex-col items-center justify-center relative overflow-hidden">
        {/* Animated background sparkles */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute text-2xl"
              initial={{
                x: Math.random() * 100 + '%',
                y: Math.random() * 100 + '%',
                opacity: 0,
              }}
              animate={{
                y: [null, '-60%'],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                delay: i * 0.4,
                ease: "easeOut",
              }}
            >
              ✨
            </motion.div>
          ))}
        </div>

        {/* Main content */}
        <motion.div
          className="relative z-10 flex flex-col items-center"
          animate={{
            scale: [1, 1.05, 1],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          {/* Animated emoji */}
          <motion.div
            className="text-6xl mb-4"
            animate={{
              rotate: [0, 10, -10, 0],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            🎬
          </motion.div>

          {/* Progress indicator */}
          <div className="relative w-20 h-20 mb-4">
            {/* Background circle */}
            <div className="absolute inset-0 border-4 border-white/30 rounded-full"></div>
            {/* Animated spinning progress circle */}
            <motion.div
              className="absolute inset-0"
              animate={{ rotate: 360 }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "linear",
              }}
            >
              <div className="absolute inset-0 border-4 border-white border-r-transparent rounded-full"></div>
            </motion.div>
            {/* Percentage text */}
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-white text-base font-black">{percentage}%</span>
            </div>
          </div>

          {/* Loading dots */}
          <div className="mb-3">
            <PulseLoader
              color="#ffffff"
              loading={true}
              size={10}
              speedMultiplier={0.8}
            />
          </div>

          {/* Status text */}
          <motion.p
            className="text-white font-bold text-base mb-1"
            animate={{ opacity: [1, 0.7, 1] }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            Creating Your Episode!
          </motion.p>
          <p className="text-white/90 text-sm font-medium">15-20 minutes remaining</p>
        </motion.div>
      </div>
    );
  }

  // Fallback to static image or placeholder
  logger.log('⚠️ Using fallback for episode:', episode.id, {
    status: episode.status,
    hasFileUrl: !!episode.file_url,
    hasThumbnail: !!episode.thumbnail_url,
    fullEpisode: episode
  });
  
  // Try thumbnail first, then fallback to logo
  const fallbackSrc = episode.thumbnail_url || '/assets/story-placeholder.png';
  
  return (
    <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
      <img
        src={fallbackSrc}
        alt={getEpisodeTitle(episode, index, null)}
        className="w-full h-full object-contain transition-all duration-700 group-hover:scale-110 group-hover:brightness-110"
        onError={(e) => {
          logger.log('❌ Image failed to load, using Pixley logo');
          e.target.src = '/assets/story-placeholder.png';
        }}
      />
      {/* Status indicator for debugging */}
      {episode.status === undefined && (
        <div className="absolute top-2 left-2 bg-yellow-500 text-black px-2 py-1 rounded text-xs font-bold">
          STATUS: UNDEFINED
        </div>
      )}
    </div>
  );
};

const ShowDetail = ({ isStoryverse = false }) => {
  const { id: showId } = useParams();
  const navigate = useNavigate();
  const { apiClient } = useAPI();
  const { subscriptionStatus, isAuthenticated, hasEntitlement } = useAuth();
  const { openWall } = useEntitlementGate();
  const { requireParentalVerification } = useProfile();
  const [show, setShow] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEpisode, setSelectedEpisode] = useState(null);
  const [isVideoPlayerOpen, setIsVideoPlayerOpen] = useState(false);
  const [showAuthWall, setShowAuthWall] = useState(false);
  const notifiedFailedEpisodes = useRef(new Set()); // Track which episodes have already shown failure notification

  const fetchEpisodes = useCallback(async () => {
    if (!apiClient || !showId) return;

    try {
      const episodesResponse = await apiClient.getStoriverseEpisodes(showId);
      if (episodesResponse.status) {
        const episodesData = episodesResponse.data;
        const newEpisodes = Array.isArray(episodesData) ? episodesData : [];
        
        // Check for failed episodes and show notifications
        newEpisodes.forEach(episode => {
          const isFailed = episode.status === 'ERROR' || 
                          episode.status === 'FAILED' || 
                          episode.status === 'error' || 
                          episode.status === 'failed';
          
          if (isFailed && !notifiedFailedEpisodes.current.has(episode.id)) {
            // Mark as notified
            notifiedFailedEpisodes.current.add(episode.id);
            
            // Show toast notification
            showToast("⚠️ Your episode couldn't be generated. Please try again.", 'error');
            
            logger.log('❌ Episode failed detected:', episode.id, episode.status);
          }
        });
        
        // Filter out failed episodes from the list
        const activeEpisodes = newEpisodes.filter(ep => {
          const isFailed = ep.status === 'ERROR' || 
                          ep.status === 'FAILED' || 
                          ep.status === 'error' || 
                          ep.status === 'failed';
          return !isFailed;
        });
        
        setEpisodes(activeEpisodes);
      }
    } catch (error) {
      logger.error('Failed to load episodes:', error);
    }
  }, [apiClient, showId]);

  // Poll for episode updates if any are processing
  useEffect(() => {
    const processingEpisodes = episodes.filter(ep => {
      const isProcessing = ep.status === 'PROCESSING' || 
                          ep.status === 'requested' || 
                          ep.status === 'pending' ||
                          ep.status === 'REQUESTED' ||
                          ep.status === 'PENDING' ||
                          !ep.file_url;
      return isProcessing;
    });

    if (processingEpisodes.length === 0) return;

    const pollInterval = setInterval(() => {
      logger.log('🔄 Polling for episode updates...');
      fetchEpisodes();
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
  }, [episodes, fetchEpisodes]);

  useEffect(() => {
    const loadData = async () => {
      if (!apiClient || !showId) return;

      try {
        setLoading(true);
        
        // Load show details
        const showResponse = await apiClient.getStoriverseShow(showId);
        if (showResponse.status) {
          const showData = showResponse.data;
          setShow(showData);
        }

        await fetchEpisodes();
      } catch (error) {
        logger.error('Failed to load show details:', error);
        showToast('Failed to load show details', 'error');
      } finally {
        setLoading(false);
      }
    };

    if (showId) {
      loadData();
    }
  }, [showId, apiClient, isStoryverse, fetchEpisodes]);


  const handlePlayEpisode = (episode) => {
    // Free-preview episodes play for everyone, guests included. Anything a
    // guest can click here is already in getVisibleEpisodes; locked cards
    // route through handleLockedEpisode instead.
    setSelectedEpisode(episode);
    setIsVideoPlayerOpen(true);
  };

  const handleLockedEpisode = () => {
    if (!isAuthenticated) {
      setShowAuthWall(true);
      return;
    }
    openWall({ trigger: "creation", contentType: "originals_episode" });
  };

  const handleStoryverseViewIncrement = useCallback((episodeId) => {
    setEpisodes((prev) =>
      prev.map((ep) =>
        ep.id === episodeId
          ? { ...ep, total_views: (Number(ep.total_views) || 0) + 1 }
          : ep
      )
    );
  }, []);

  const closeVideoPlayer = () => {
    setIsVideoPlayerOpen(false);
    setSelectedEpisode(null);
  };

  const handleNextEpisode = () => {
    if (!selectedEpisode) return;
    
    const currentIndex = episodes.findIndex(ep => ep.id === selectedEpisode.id);
    if (currentIndex < episodes.length - 1) {
      const nextEpisode = episodes[currentIndex + 1];
      
      // Check if user is premium
      const isUserPremium = subscriptionStatus?.subscriptionType !== 'NONE';
      
      // Check if next episode is premium
      const isNextEpisodePremium = isEpisodePremium(nextEpisode, currentIndex + 1);
      
      // Allow navigation if:
      // 1. User is premium (can access any episode), OR
      // 2. User is not premium but next episode is not premium
      if (isUserPremium || !isNextEpisodePremium) {
        setSelectedEpisode(nextEpisode);
      } else {
        // Show upgrade prompt for premium episode
        showToast('This episode requires Premium access. Upgrade to continue watching!', 'warning');
      }
    }
  };

  const isEpisodePremium = (episode, episodeIndex) => {
    // Prefer explicit flags from backend when present
    if (episode?.is_premium !== undefined) return !!episode.is_premium;
    if (episode?.premium !== undefined) return !!episode.premium;
    if (episode?.subscription_required !== undefined) return !!episode.subscription_required;

    // Fallback for Storyverse: episodes after the first are premium
    if (isStoryverse) return episodeIndex >= 1;

    // Additional hint
    if (episode?.premium_content !== undefined) return !!episode.premium_content;

    // Default
    return false;
  };

  const handlePreviousEpisode = () => {
    if (!selectedEpisode) return;
    
    const currentIndex = episodes.findIndex(ep => ep.id === selectedEpisode.id);
    if (currentIndex > 0) {
      setSelectedEpisode(episodes[currentIndex - 1]);
    }
  };

  const getCurrentEpisodeIndex = () => {
    if (!selectedEpisode) return -1;
    return episodes.findIndex(ep => ep.id === selectedEpisode.id);
  };

  // Filter episodes based on subscription status for Storyverse shows
  // Originals preview policy: the first 2 episodes are free to watch for
  // EVERYONE (guests included — the show page no longer auth-walls on open);
  // episodes 3+ need a membership. Members see everything.
  const FREE_PREVIEW_EPISODES = 2;
  const canWatchAll = subscriptionStatus?.subscriptionType !== 'NONE';

  const getVisibleEpisodes = () => {
    if (canWatchAll) return episodes;
    return episodes.slice(0, FREE_PREVIEW_EPISODES);
  };

  const getLockedEpisodes = () => {
    if (canWatchAll) return [];
    return episodes.slice(FREE_PREVIEW_EPISODES);
  };


  if (loading) {
    return <LoadingScreen message="Loading show details..." type="show" />;
  }

  if (!show) {
    return (
      <div className="min-h-screen" style={{ background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)", fontFamily: "'Inter', 'Neue Montreal', 'Fredoka', sans-serif" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-32">
          <div className="text-center py-16">
            <div className="text-6xl mb-6">😕</div>
            <h1 style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 30, fontWeight: 700, color: "#0B1A33", marginBottom: 16 }}>Show Not Found</h1>
            <p style={{ color: "#5A6B85", marginBottom: 32 }}>The show you're looking for doesn't exist.</p>
            <button
              onClick={() => navigate('/storyverse')}
              className="inline-block transition active:scale-[0.97]"
              style={{ fontFamily: "'Fredoka', sans-serif", fontWeight: 700, fontSize: 15, color: "#FFFFFF", background: "#0065F8", border: "none", borderRadius: 999, padding: "12px 24px", boxShadow: "0px 4px 0px #073B8E", cursor: "pointer" }}
            >
              Back to Shows
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)", fontFamily: "'Inter', 'Neue Montreal', 'Fredoka', sans-serif" }}>
      {/* Soft brand blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div style={{ position: "absolute", top: -180, left: -160, width: 460, height: 460, borderRadius: "50%", background: "rgba(0,101,248,0.08)", filter: "blur(40px)" }} />
        <div style={{ position: "absolute", bottom: -200, right: -180, width: 480, height: 480, borderRadius: "50%", background: "rgba(0,202,255,0.08)", filter: "blur(40px)" }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-32">
        {/* Back button */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/storyverse')}
            className="inline-flex items-center gap-2 transition active:scale-[0.97]"
            style={{ fontFamily: "'Fredoka', sans-serif", background: "#FFFFFF", color: "#0065F8", fontWeight: 700, fontSize: 14, border: "2px solid rgba(0,101,248,0.18)", borderRadius: 999, padding: "10px 18px", boxShadow: "0px 3px 0px rgba(11,26,51,0.06)", cursor: "pointer" }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.4} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Shows
          </button>
        </div>

        {/* Show Header */}
        <div className="mb-12">
          <div className="flex flex-col lg:flex-row gap-8 items-start">
            {/* Show Thumbnail */}
            <div className="flex-shrink-0">
              <div className="relative w-80 h-48 rounded-2xl overflow-hidden" style={{ boxShadow: "0px 2px 0px rgba(11,26,51,0.06), 0px 12px 28px rgba(11,26,51,0.10)", border: "2px solid rgba(11,26,51,0.06)" }}>
                <img
                  src={getOriginalsBranding(show)?.cover || show.cover_image_url || '/assets/story-placeholder.png'}
                  alt={show.name || show.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = '/assets/story-placeholder.png';
                  }}
                />
              </div>
            </div>

            {/* Show Info */}
            <div className="flex-1 space-y-4">
              <h1 style={{ fontFamily: "'Fredoka', sans-serif", fontSize: "clamp(2rem, 4vw, 2.75rem)", fontWeight: 700, color: "#0065F8", lineHeight: 1.1 }}>
                {show.name || show.title || 'Untitled Show'}
              </h1>

              <div className="flex items-center gap-4" style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 14, fontWeight: 600, color: "#5A6B85" }}>
                <span className="flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {episodes.length} episodes
                </span>
              </div>

              {show.description && (
                <p style={{ color: "#334155", fontSize: 17, lineHeight: 1.55, maxWidth: "42rem", margin: 0 }}>
                  {show.description}
                </p>
              )}

              {show.characters && show.characters.length > 0 && (
                <div className="flex items-center gap-2">
                  <span style={{ fontSize: 14, color: "#94A3B8", fontWeight: 600 }}>Characters:</span>
                  <div className="flex items-center gap-1.5" style={{ fontSize: 13, fontWeight: 700, color: "#0065F8", background: "#EAF3FF", padding: "6px 14px", borderRadius: 999, boxShadow: "0px 2px 0px rgba(11,26,51,0.06)" }}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                    </svg>
                    {show.characters.length} characters
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Episodes Section */}
        <div>
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <h2 style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 28, fontWeight: 700, color: "#0B1A33" }}>Episodes</h2>
              {isStoryverse && subscriptionStatus?.subscriptionType === 'NONE' && getLockedEpisodes().length > 0 && (
                <div className="flex items-center gap-2" style={{ padding: "5px 12px", background: "#E7F0FF", border: "1px solid rgba(0,101,248,0.25)", borderRadius: 999 }}>
                  <svg className="w-4 h-4" style={{ color: "#0065F8" }} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <span style={{ color: "#0065F8", fontSize: 13, fontWeight: 700 }}>
                    {getLockedEpisodes().length} Members-Only Episodes
                  </span>
                </div>
              )}
            </div>
          </div>

          {episodes.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Render visible episodes */}
              {getVisibleEpisodes().map((episode, index) => (
                <div
                  key={episode.id}
                  className="group relative rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 flex flex-col h-full"
                  style={{ background: "#FFFFFF", border: "2px solid rgba(11,26,51,0.06)", boxShadow: "0px 2px 0px rgba(11,26,51,0.06), 0px 12px 28px rgba(11,26,51,0.10)" }}
                  onMouseEnter={(e) => {
                    const video = e.currentTarget.querySelector('video');
                    if (video) {
                      video.currentTime = 0;
                      video.play().catch(() => {
                        // Silently handle autoplay failures
                      });
                    }
                  }}
                  onMouseLeave={(e) => {
                    const video = e.currentTarget.querySelector('video');
                    if (video) {
                      video.pause();
                      video.currentTime = 0;
                    }
                  }}
                >
                  {/* Episode Thumbnail */}
                  <div className="relative aspect-video overflow-hidden" style={{ background: "#0B1A33" }}>
                    {renderEpisodeThumbnail(episode, index, isStoryverse)}
                    
                    {/* Play button overlay - only show if episode is generated */}
                    {episode.status === 'GENERATED' && (
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 bg-black/20">
                      <button
                        onClick={() => handlePlayEpisode(episode)}
                        className="w-14 h-14 bg-white/95 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white hover:scale-110 transition-all duration-200 shadow-lg"
                      >
                        <svg className="w-6 h-6 text-gray-800 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </button>
                      </div>
                    )}

                    {/* Episode number badge */}
                    <div className="absolute top-3 left-3 bg-black/70 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-medium">
                      Episode {index + 1}
                    </div>
                  </div>

                  {/* Episode info */}
                  <div className="p-6 flex flex-col flex-1">
                    <div className="space-y-2">
                      <h3 className="line-clamp-2" style={{ fontFamily: "'Fredoka', sans-serif", fontWeight: 700, fontSize: 18, color: "#0B1A33", lineHeight: 1.2 }}>
                        {getEpisodeTitle(episode, index, show)}
                      </h3>
                      {episode.description && (
                        <p className="line-clamp-2" style={{ fontSize: 14, color: "#334155", lineHeight: 1.5, margin: 0 }}>
                          {episode.description}
                        </p>
                      )}
                      <p style={{ fontSize: 12, color: "#94A3B8", margin: 0 }}>
                        {episode.created_at ? new Date(episode.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        }) : 'No date'}
                        {isStoryverse && (
                          <span className="ml-2">
                            · {(Number(episode.total_views) || 0).toLocaleString()} views
                          </span>
                        )}
                      </p>
                    </div>

                    {/* Action buttons — pinned to the bottom so they align across cards */}
                    <div className="flex items-center gap-2 mt-auto pt-4">
                      <button
                        onClick={() => handlePlayEpisode(episode)}
                        className="flex-1 transition active:scale-[0.97]"
                        style={{ fontFamily: "'Fredoka', sans-serif", fontWeight: 700, fontSize: 14, color: "#FFFFFF", background: "#0065F8", border: "none", borderRadius: 14, padding: "12px 16px", boxShadow: "0px 4px 0px #073B8E", cursor: "pointer" }}
                      >
                        Watch Episode
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Render locked episodes for free users in Storyverse */}
              {isStoryverse && getLockedEpisodes().length > 0 && (
                getLockedEpisodes().map((episode, index) => {
                  const actualIndex = getVisibleEpisodes().length + index;
                  return (
                    <div
                      key={`locked-${episode.id}`}
                      className="group relative rounded-2xl overflow-hidden flex flex-col h-full"
                      style={{ background: "#FFFFFF", border: "2px solid rgba(11,26,51,0.06)", boxShadow: "0px 2px 0px rgba(11,26,51,0.06), 0px 12px 28px rgba(11,26,51,0.10)" }}
                    >
                      {/* Locked Episode Thumbnail */}
                      <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-gray-800 to-gray-900">
                        {/* Blurred background using the episode thumbnail */}
                        <div className="absolute inset-0 filter blur-sm">
                          {renderEpisodeThumbnail(episode, actualIndex, isStoryverse)}
                        </div>
                        
                        {/* Lock overlay */}
                        <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                          <div className="text-center">
                            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)", boxShadow: "0px 3px 0px #073B8E" }}>
                              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                              </svg>
                            </div>
                            <p className="text-white font-semibold text-sm mb-2">Members Only</p>
                            <p className="text-gray-300 text-xs">Join Pixley to watch</p>
                          </div>
                        </div>

                        {/* Episode number badge */}
                        <div className="absolute top-3 left-3 bg-black/70 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-medium">
                          Episode {actualIndex + 1}
                        </div>

                        {/* Premium badge */}
                        <div className="absolute top-3 right-3 text-white px-2 py-1 rounded-full text-xs font-bold" style={{ background: "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)" }}>
                          PREMIUM
                        </div>
                      </div>

                      {/* Locked Episode info */}
                      <div className="p-6 flex flex-col flex-1">
                        <div className="space-y-2">
                          <h4 className="line-clamp-2" style={{ fontFamily: "'Fredoka', sans-serif", fontWeight: 700, fontSize: 18, color: "#0B1A33", lineHeight: 1.2 }}>
                            {getEpisodeTitle(episode, actualIndex, show)}
                          </h4>
                          <p className="line-clamp-2" style={{ fontSize: 14, color: "#5A6B85", lineHeight: 1.5, margin: 0 }}>
                            {episode.description || 'Unlock with Premium to watch this episode and discover what happens next!'}
                          </p>
                          <p style={{ fontSize: 12, color: "#94A3B8", margin: 0 }}>
                            {(Number(episode.total_views) || 0).toLocaleString()} views
                          </p>
                        </div>

                        <div className="flex items-center gap-2 mt-auto pt-4">
                          <button
                            onClick={handleLockedEpisode}
                            className="flex-1 transition active:scale-[0.97]"
                            style={{ fontFamily: "'Fredoka', sans-serif", fontWeight: 700, fontSize: 14, color: "#FFFFFF", background: "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)", border: "none", borderRadius: 14, padding: "12px 16px", boxShadow: "0px 4px 0px #073B8E", cursor: "pointer" }}
                          >
                            Watch with Pixley
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="text-6xl mb-6">🎬</div>
              <h3 style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 24, fontWeight: 700, color: "#0B1A33", marginBottom: 16 }}>No Episodes Yet</h3>
              <p className="max-w-md mx-auto" style={{ color: "#5A6B85", marginBottom: 32 }}>
                This show doesn't have any episodes yet. Check back soon!
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Video Player Modal */}
      {selectedEpisode && (
        <VideoPlayer
          isOpen={isVideoPlayerOpen}
          onClose={closeVideoPlayer}
          videoUrl={selectedEpisode.file_url}
          title={`${show?.name || 'Show'} - ${selectedEpisode.title || `Episode ${getCurrentEpisodeIndex() + 1}`}`}
          description={selectedEpisode.description}
          onNext={handleNextEpisode}
          onPrevious={handlePreviousEpisode}
          hasNext={getCurrentEpisodeIndex() < episodes.length - 1}
          hasPrevious={getCurrentEpisodeIndex() > 0}
          episodeId={selectedEpisode.id}
          showId={show?.id || showId}
          storyId={selectedEpisode.id}
          isStoryverse={isStoryverse}
          onStoryverseViewIncrement={isStoryverse ? handleStoryverseViewIncrement : undefined}
          outro={isStoryverse ? getOriginalsOutro(show) : null}
          showName={show?.name || null}
          nextEpisode={(() => {
            const nxt = episodes[getCurrentEpisodeIndex() + 1];
            return nxt ? { title: nxt.title || nxt.name || null, thumbnailUrl: nxt.thumbnail_url || null } : null;
          })()}
          currentThumbnailUrl={selectedEpisode.thumbnail_url || null}
        />
      )}

      {/* Auth Wall for Storyverse Originals */}
      <AuthWall
        isOpen={showAuthWall}
        onClose={() => setShowAuthWall(false)}
        feature="storyverse_original"
        message="Sign up to watch Storyverse Originals"
      />
    </div>
  );
};

export default ShowDetail;